// The refresh-token exchange, shared by both front ends (ARCH-17).
//
// ARCH-18: this is a correctness requirement, not tidiness. Refresh tokens rotate and the server
// revokes the whole session when it sees one replayed (`ReplayDetected`). Two independently
// written refresh clients sharing one session will eventually race a rotation, and the loser's
// replay ends the session for both — so the user is thrown out of payroll by a bug in accounting.
// One implementation, one in-flight promise, one dead-session latch.
//
// What is deliberately NOT here: the toast and the redirect. How an app tells its user the session
// ended is that app's business; what "ended" means is not.

/** The token pair was renewed. */
export const REFRESH_RENEWED = 'renewed'

/** The server refused the refresh token — the session is over. */
export const REFRESH_REJECTED = 'rejected'

/** The server could not be reached — says nothing about the session, so keep it. */
export const REFRESH_UNAVAILABLE = 'unavailable'

/**
 * Builds a refresh client over an app's own HTTP and token plumbing.
 *
 * @param {object} options
 * @param {object} options.http          Axios-like client, already pointed at the API.
 * @param {object} options.tokens        getRefreshToken / saveToken / saveRefreshToken.
 * @param {Function} [options.decodeResponse] Turns a response body into a JSON string.
 * @param {Function} [options.log]       Debug sink.
 */
export function createRefreshClient({ http, tokens, decodeResponse = JSON.stringify, log = () => {} }) {
    // Holds a Promise while a refresh is in progress, so concurrent 401s share one call instead
    // of each firing its own — which is what would race the server's rotation.
    let refreshInFlight = null

    // Latched once a refresh has been refused, so later 401s skip straight to the session-ended
    // path rather than each retrying an exchange that cannot succeed.
    let sessionDead = false

    return {
        /** Whether a refresh has already been refused for this session. */
        isSessionDead: () => sessionDead,

        /** Latches the session as over — used by the app when it ends the session itself. */
        markSessionDead() {
            sessionDead = true
        },

        /** Test seam: forget the latch and any in-flight call. */
        reset() {
            refreshInFlight = null
            sessionDead = false
        },

        /**
         * Exchanges the stored refresh token for a fresh pair.
         *
         * Never rejects — it resolves an outcome — so callers have no error to handle and no
         * unobserved rejection can escape.
         *
         * @returns {Promise<string>} One of the REFRESH_* outcomes.
         */
        refresh() {
            if (refreshInFlight) return refreshInFlight

            const refreshToken = tokens.getRefreshToken()
            if (!refreshToken) return Promise.resolve(REFRESH_REJECTED)

            // The endpoint binds [FromForm] string refreshToken — a JSON body leaves it unbound
            // and the server reports "RefreshToken field is required".
            const body = new FormData()
            body.append('refreshToken', refreshToken)

            refreshInFlight = http
                .put('/api/Auth/RefreshToken', body, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                })
                .then((response) => {
                    const data = JSON.parse(decodeResponse(response.data, '/api/Auth/RefreshToken'))
                    if (!data || !data.accessToken || !data.refreshToken) return REFRESH_REJECTED
                    tokens.saveToken(data.accessToken)
                    tokens.saveRefreshToken(data.refreshToken)
                    return REFRESH_RENEWED
                })
                .catch((error) => {
                    const status = error?.response?.status
                    log(`refresh failed: ${JSON.stringify(status)}`)

                    // Only the server refusing the token means the session is over. A dropped
                    // connection, a timeout or a 5xx says nothing about it, and treating those
                    // alike logged people out for a momentary network problem — discarding a
                    // refresh token that was still perfectly good.
                    if (status === 401 || status === 403 || status === 404) {
                        sessionDead = true
                        return REFRESH_REJECTED
                    }
                    return REFRESH_UNAVAILABLE
                })
                .then((outcome) => {
                    refreshInFlight = null
                    return outcome
                })

            return refreshInFlight
        },
    }
}
